import React, { useMemo } from 'react';
import {
  AlertTriangle,
  RotateCw,
  Play,
  Clock,
  MapPin,
  Zap,
  Shield,
  Brain
} from 'lucide-react';
import {
  StatCard,
  ImpairmentMeter,
  GameButton,
  CampaignBadge,
  RoadcastLogo
} from './roadcastUI';

/**
 * CrashCard — Roadcast redesign of the crash / result screen.
 *
 * Contract kept identical to the previous version so the rest of the game
 * doesn't need to change:
 *  - fact       : { fact: string, source?: string }
 *  - stats      : { timeSurvived, distanceTraveled, crashes }
 *  - drinkCount : number  (0 = sober, 3 = buzzed, 6 = very drunk)
 *  - onRestart  : () => void   (retries the run)
 *  - onContinue : () => void   (continues with same run)
 */
const CrashCard = ({ fact, stats, drinkCount, onRestart, onContinue }) => {
  // Dynamic BAC — same calculation used previously in the game.
  const bac = useMemo(() => Number((drinkCount * 0.02).toFixed(2)), [drinkCount]);
  const bacText = `${bac.toFixed(2)}%`;

  // Map BAC → meter fill (0.00 → 0, 0.15+ → 1) and risk label.
  const impairmentPercent = Math.min(1, bac / 0.15);
  const riskLabel =
    bac === 0 ? 'CLEAR' :
    bac < 0.03 ? 'LOW RISK' :
    bac < 0.06 ? 'MODERATE RISK' :
    bac < 0.10 ? 'ELEVATED RISK' :
    'CRITICAL RISK';

  const drinksLabel =
    drinkCount === 0 ? '0 DRINKS' :
    drinkCount <= 3 ? `${drinkCount} DRINKS` :
    `${drinkCount}+ DRINKS`;

  return (
    <div
      className="absolute inset-0 z-50 rc-crash-shake"
      data-testid="crash-card"
      style={{
        overflow: 'hidden',
        color: 'var(--rc-text-primary)',
        fontFamily: 'var(--rc-font-display)'
      }}
    >
      {/* Background — crash aftermath (dark, wet street, subtle emergency lights) */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?auto=format&fit=crop&w=1920&q=80')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'grayscale(0.35) brightness(0.5)'
        }}
      />
      {/* Cracked glass overlay tint + red / teal ambient */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at 15% 60%, rgba(255,91,106,0.14) 0%, transparent 45%),' +
            'radial-gradient(ellipse at 82% 40%, rgba(34,211,195,0.10) 0%, transparent 55%),' +
            'linear-gradient(180deg, rgba(2,6,8,0.75) 0%, rgba(3,8,10,0.9) 50%, rgba(2,6,8,0.95) 100%)'
        }}
      />
      <CrackedGlassOverlay />
      <div className="rc-vignette" />
      <div className="rc-corner-frame"><span /><span /></div>

      {/* Top-right chip: "DRINK. DRIVE. PAY." (desktop/tablet) */}
      <div
        className="rc-desktop-hud"
        data-testid="drink-drive-pay-chip"
        style={{
          position: 'absolute',
          top: 18,
          right: 22,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          zIndex: 5
        }}
      >
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: '50%',
            border: '1.5px solid var(--rc-danger)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(20,4,7,0.75)'
          }}
        >
          <NoDrinkIcon />
        </div>
        <div style={{ lineHeight: 1.15 }}>
          <div
            style={{
              fontFamily: 'var(--rc-font-display)',
              fontWeight: 900,
              fontSize: 12,
              letterSpacing: '0.18em',
              color: 'var(--rc-text-primary)'
            }}
          >
            DRINK. DRIVE. PAY.
          </div>
          <div
            style={{
              fontSize: 10,
              letterSpacing: '0.22em',
              color: 'var(--rc-danger)',
              fontFamily: 'var(--rc-font-mono)'
            }}
          >
            IT&apos;S NEVER WORTH IT
          </div>
        </div>
      </div>

      {/* Top-left RoadCast brand lockup (desktop/tablet) */}
      <div
        className="rc-desktop-hud"
        data-testid="crash-brand-logo-wrapper"
        style={{
          position: 'absolute',
          top: 18,
          left: 22,
          zIndex: 5,
          background: 'rgba(9, 20, 24, 0.72)',
          border: '1px solid var(--rc-border-subtle)',
          borderRadius: 'var(--rc-radius-md)',
          padding: '8px 14px',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          boxShadow: 'var(--rc-panel-glow)'
        }}
      >
        <RoadcastLogo height={32} testId="crash-brand-logo" />
      </div>

      {/* Center content */}
      <div className="rc-card-modal-container">
        {/* Warning title section */}
        <div className="rc-enter" style={{ textAlign: 'center', marginTop: 4 }}>
          <div
            className="hidden sm:inline-flex"
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              alignItems: 'center',
              justifyContent: 'center',
              border: '1.5px solid var(--rc-danger)',
              color: 'var(--rc-danger)',
              background: 'rgba(30,6,10,0.7)',
              boxShadow: '0 0 22px rgba(255,91,106,0.35)',
              marginBottom: 2
            }}
          >
            <AlertTriangle size={16} strokeWidth={2} />
          </div>
          <h1
            style={{
              fontFamily: 'var(--rc-font-display)',
              fontWeight: 900,
              letterSpacing: '-0.02em',
              fontSize: 'clamp(20px, 4vw, 52px)',
              lineHeight: 1.05,
              margin: 0
            }}
          >
            <span style={{ color: 'var(--rc-text-primary)' }}>CRASHED </span>
            <span
              style={{
                color: 'var(--rc-danger)',
                textShadow: '0 0 22px rgba(255,91,106,0.45)'
              }}
            >
              OUT
            </span>
          </h1>
          <div
            data-testid="crash-bac-line"
            style={{
              marginTop: 2,
              fontFamily: 'var(--rc-font-mono)',
              fontSize: 'clamp(9px, 1vw, 12px)',
              letterSpacing: '0.18em',
              color: 'var(--rc-text-secondary)',
              textTransform: 'uppercase'
            }}
          >
            ESTIMATED BAC:{' '}
            <span style={{ color: 'var(--rc-danger)', fontWeight: 700 }}>{bacText}</span>{' '}
            <span style={{ color: 'var(--rc-text-muted)', margin: '0 4px' }}>•</span>{' '}
            {drinksLabel}
          </div>
        </div>

        {/* Message panel */}
        <div
          className="rc-panel rc-enter rc-enter-delay-1"
          style={{
            padding: '8px 14px',
            width: 'min(720px, 98%)',
            textAlign: 'center'
          }}
        >
          <div
            style={{
              fontSize: 'clamp(12px, 1.3vw, 18px)',
              fontWeight: 700,
              marginBottom: 2,
              color: 'var(--rc-text-primary)'
            }}
          >
            You thought you had control.
          </div>
          <div
            style={{
              fontSize: 'clamp(14px, 1.5vw, 22px)',
              fontWeight: 800,
              color: 'var(--rc-teal-bright)',
              textShadow: '0 0 14px rgba(34,211,195,0.35)',
              marginBottom: 4
            }}
            data-testid="crash-highlight"
          >
            Alcohol thought otherwise.
          </div>
          <div
            style={{
              fontSize: 'clamp(10px, 1vw, 13px)',
              color: 'var(--rc-text-secondary)',
              lineHeight: 1.35
            }}
            data-testid="crash-fact"
          >
            {fact?.fact || 'Alcohol impairs your ability to drive safely. Never drink and drive.'}
          </div>
          {fact?.source && (
            <div
              className="rc-tag"
              style={{ marginTop: 4, fontSize: 8 }}
            >
              Source: {fact.source}
            </div>
          )}
        </div>

        {/* Stat cards */}
        <div
          className="rc-enter rc-enter-delay-2"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 'clamp(4px, 1vw, 10px)',
            width: 'min(760px, 98%)'
          }}
        >
          <StatCard
            label="Time Survived"
            value={`${stats.timeSurvived.toFixed(1)}s`}
            icon={<Clock size={16} strokeWidth={1.8} />}
            testId="crash-time"
          />
          <StatCard
            label="Distance"
            value={`${stats.distanceTraveled.toFixed(1)} m`}
            icon={<MapPin size={16} strokeWidth={1.8} />}
            testId="crash-distance"
          />
          <StatCard
            label="Total Crashes"
            value={stats.crashes}
            icon={<Zap size={16} strokeWidth={1.8} />}
            testId="crash-count"
          />
        </div>

        {/* Action buttons */}
        <div
          className="rc-enter rc-enter-delay-3"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 8,
            marginTop: 2
          }}
        >
          <GameButton
            variant="primary"
            onClick={onRestart}
            icon={<RotateCw size={14} strokeWidth={2.5} />}
            testId="restart-simulation-button"
          >
            Retry Run
          </GameButton>
          <GameButton
            variant="secondary"
            onClick={onContinue}
            icon={<Play size={14} fill="currentColor" />}
            testId="continue-simulation-button"
          >
            Continue
          </GameButton>
        </div>

        {/* Safety copy */}
        <div
          className="rc-enter rc-enter-delay-4"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            color: 'var(--rc-text-secondary)',
            fontSize: 'clamp(10px, 0.9vw, 12px)',
            textAlign: 'center',
            maxWidth: 720
          }}
          data-testid="crash-safety-copy"
        >
          <Shield size={14} color="var(--rc-teal)" />
          <span>
            If you plan to drink, arrange a sober driver, use public transport, or call a rideshare.
          </span>
        </div>

        {/* Campaign badge */}
        <div className="rc-enter rc-enter-delay-4" style={{ marginBottom: 12 }}>
          <CampaignBadge />
        </div>
      </div>
    </div>
  );
};

const NoDrinkIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--rc-danger)" strokeWidth="1.7" strokeLinecap="round">
    <path d="M8 3h6l1 6c0 3 -2 5 -4 5s-4 -2 -4 -5z" />
    <path d="M10 14v6" />
    <path d="M7 20h6" />
    <line x1="4" y1="4" x2="20" y2="20" strokeWidth="2" />
  </svg>
);

/** Very subtle SVG cracked-glass overlay — non-graphic, purely decorative. */
const CrackedGlassOverlay = () => (
  <svg
    aria-hidden
    style={{
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      opacity: 0.14,
      mixBlendMode: 'screen',
      pointerEvents: 'none'
    }}
    viewBox="0 0 1600 900"
    preserveAspectRatio="xMidYMid slice"
  >
    <g stroke="#ffffff" strokeWidth="0.6" fill="none">
      {/* Left impact site */}
      <g transform="translate(280 320)">
        <circle r="6" fill="#ffffff" opacity="0.35" />
        {Array.from({ length: 14 }).map((_, i) => {
          const a = (i / 14) * Math.PI * 2;
          const r1 = 8;
          const r2 = 220 + Math.random() * 120;
          return (
            <line
              key={i}
              x1={Math.cos(a) * r1}
              y1={Math.sin(a) * r1}
              x2={Math.cos(a) * r2}
              y2={Math.sin(a) * r2}
              opacity="0.6"
            />
          );
        })}
        {[40, 80, 130, 200].map((r) => (
          <circle key={r} r={r} opacity={0.25} />
        ))}
      </g>
      {/* Right impact site */}
      <g transform="translate(1350 220)">
        <circle r="4" fill="#ffffff" opacity="0.3" />
        {Array.from({ length: 10 }).map((_, i) => {
          const a = (i / 10) * Math.PI * 2;
          const r2 = 180 + Math.random() * 80;
          return (
            <line
              key={i}
              x1={0}
              y1={0}
              x2={Math.cos(a) * r2}
              y2={Math.sin(a) * r2}
              opacity="0.5"
            />
          );
        })}
      </g>
    </g>
  </svg>
);

export default CrashCard;
